/**
 * Firebase App Check initialization.
 *
 * App Check protects the /api/auth/** endpoints from abuse — every request
 * must carry a valid X-Firebase-AppCheck header obtained from the reCAPTCHA
 * Enterprise provider. Without a valid token the server returns 401
 * app_check_failed before performing any cookie read or Google API call
 * (Decision 7 / Abuse Mitigation Configuration).
 *
 * Debug mode: if VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN is set (local dev /
 * test environments), we install it as `globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN`
 * BEFORE calling initializeAppCheck so the Firebase SDK picks it up
 * automatically and skips the reCAPTCHA evaluation.
 *
 * If any required env var is missing at runtime, App Check initialisation is
 * skipped and getAppCheckToken() returns null. The server will respond with
 * 401 app_check_failed, which coldLoad.ts surfaces as 'app_check_failed' /
 * 'browser_blocked'. This lets dev environments without a real reCAPTCHA key
 * fail gracefully rather than crashing the whole app.
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  getToken,
  type AppCheck,
} from 'firebase/app-check';

let appCheckInstance: AppCheck | null = null;

// ── App Check init readiness ──────────────────────────────────────────────────
//
// Callers in serverRefresh.ts await this promise before issuing any
// /api/auth/* request, so the first cold-load refresh / exchange does not
// race ahead of App Check init and receive a spurious 401 app_check_failed
// (the 2026-05-23 ycb10133030 incident). The promise resolves on EVERY exit
// path of initFirebaseAppCheck — success, env-var miss, or thrown error —
// and NEVER rejects.

let _resolveAppCheckReady!: () => void;
export const appCheckReady: Promise<void> = new Promise<void>((resolve) => {
  _resolveAppCheckReady = resolve;
});

/**
 * Initialize Firebase and App Check. Non-blocking — should be called from
 * main.tsx without await. Idempotent (safe to call multiple times).
 */
export async function initFirebaseAppCheck(): Promise<void> {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const appId = import.meta.env.VITE_FIREBASE_APP_ID;
  const recaptchaSiteKey = import.meta.env.VITE_FIREBASE_RECAPTCHA_SITE_KEY;

  try {
    if (!apiKey || !projectId || !appId || !recaptchaSiteKey) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Mochia/firebase] Missing VITE_FIREBASE_* env vars — App Check disabled. ' +
          'Server will reject /api/auth/* calls with 401 app_check_failed.',
      );
      return;
    }

    // Install debug token BEFORE initializeAppCheck so the SDK picks it up.
    const debugToken = import.meta.env.VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN;
    if (debugToken) {
      // @ts-expect-error — Firebase debug token global is not in standard types
      globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
    }

    // Reuse an existing Firebase app if already initialized.
    let app: FirebaseApp;
    const existingApps = getApps();
    if (existingApps.length > 0) {
      app = existingApps[0]!;
    } else {
      app = initializeApp({ apiKey, projectId, appId });
    }

    if (appCheckInstance) return; // already initialized

    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[Mochia/firebase] App Check initialization failed:', err);
  } finally {
    // Always resolve readiness — serverRefresh.ts awaits this; a never-resolving
    // promise would freeze the auth flow forever. On the missing-env-vars and
    // thrown-error paths we resolve with no instance so subsequent
    // getAppCheckToken() calls return null and the request goes out without
    // the header, surfacing as 401 app_check_failed with structured logging.
    _resolveAppCheckReady();
  }
}

/**
 * Obtain a fresh App Check token string, or null if App Check is not
 * initialized or token retrieval fails. The null case is handled by
 * serverRefresh.ts — requests go out without the header and the server
 * responds with 401 app_check_failed.
 *
 * Sharp edge: getToken() can HANG indefinitely if the reCAPTCHA Enterprise
 * script fails to load (CSP block, network error, ITP iframe block). A
 * hung await would freeze the AuthCallbackScreen on "正在完成登入" forever.
 * We race against a 10s timeout so the failure surfaces as 401
 * app_check_failed → LoginScreen with actionable copy.
 */
const APP_CHECK_TOKEN_TIMEOUT_MS = 10_000;

export async function getAppCheckToken(): Promise<string | null> {
  if (!appCheckInstance) return null;
  const tokenPromise = getToken(appCheckInstance, /* forceRefresh */ false)
    .then((r) => r.token)
    .catch(() => null);
  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => resolve(null), APP_CHECK_TOKEN_TIMEOUT_MS);
  });
  return Promise.race([tokenPromise, timeoutPromise]);
}
